import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { verifyBiometric } from './biometric'
import {
  clearLock,
  createLock,
  readLock,
  verifyPin,
  writeLock,
  type LockConfig,
  type VerifyResult,
} from './lock'

const CHECK_EVERY = 15_000

/** Connexion par mot de passe à l'instant : inutile de redemander le code juste après. */
let freshSignIn = false
export function markFreshSignIn(): void {
  freshSignIn = true
}

/**
 * État du verrouillage de l'application pour le compte connecté : verrouillée au lancement,
 * au retour d'arrière-plan et après le délai d'inactivité choisi.
 */
export const useLockStore = defineStore('lock', () => {
  const userId = ref<string | null>(null)
  const config = ref<LockConfig | null>(null)
  const locked = ref(false)

  const enabled = computed(() => config.value !== null)

  let lastActive = Date.now()
  let hiddenAt: number | null = null
  let listening = false

  function delay(): number {
    return (config.value?.delayMinutes ?? 5) * 60_000
  }

  function touch() {
    if (!locked.value) lastActive = Date.now()
  }

  function onVisibility() {
    if (!config.value) return
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      if (config.value.delayMinutes === 0) locked.value = true
      return
    }
    if (hiddenAt !== null && Date.now() - hiddenAt >= delay()) locked.value = true
    hiddenAt = null
    touch()
  }

  function listen() {
    if (listening) return
    listening = true
    for (const event of ['pointerdown', 'keydown', 'touchstart', 'scroll']) {
      window.addEventListener(event, touch, { passive: true })
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.setInterval(() => {
      if (config.value && !locked.value && Date.now() - lastActive >= Math.max(delay(), 60_000)) {
        locked.value = true
      }
    }, CHECK_EVERY)
  }

  /** À l'ouverture de l'espace connecté : verrouille si un code existe pour ce compte. */
  function start(id: string) {
    if (userId.value === id) return
    userId.value = id
    config.value = readLock(id)
    locked.value = config.value !== null && !freshSignIn
    freshSignIn = false
    lastActive = Date.now()
    listen()
  }

  function stop() {
    userId.value = null
    config.value = null
    locked.value = false
  }

  async function unlock(pin: string): Promise<VerifyResult> {
    const id = userId.value
    if (!id) return { ok: true }
    const result = await verifyPin(id, pin)
    config.value = readLock(id)
    if (result.ok) {
      locked.value = false
      lastActive = Date.now()
    }
    return result
  }

  async function unlockWithBiometric(): Promise<boolean> {
    const credential = config.value?.credentialId
    if (!credential || !userId.value) return false
    const ok = await verifyBiometric(credential)
    if (ok) {
      writeLock(userId.value, { ...(config.value as LockConfig), failures: 0, blockedUntil: null })
      config.value = readLock(userId.value)
      locked.value = false
      lastActive = Date.now()
    }
    return ok
  }

  async function setPin(pin: string) {
    const id = userId.value
    if (!id) return
    config.value = await createLock(id, pin, config.value ?? undefined)
    lastActive = Date.now()
  }

  function update(changes: Partial<Pick<LockConfig, 'delayMinutes' | 'credentialId'>>) {
    const id = userId.value
    if (!id || !config.value) return
    config.value = { ...config.value, ...changes }
    writeLock(id, config.value)
  }

  function disable() {
    if (userId.value) clearLock(userId.value)
    config.value = null
    locked.value = false
  }

  /** Vérifie le code actuel sans changer l'état verrouillé (changement ou désactivation). */
  async function check(pin: string): Promise<VerifyResult> {
    const id = userId.value
    if (!id) return { ok: true }
    const result = await verifyPin(id, pin)
    config.value = readLock(id)
    return result
  }

  return {
    config,
    enabled,
    locked,
    start,
    stop,
    unlock,
    unlockWithBiometric,
    setPin,
    update,
    disable,
    check,
  }
})
